import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  LOGOUT_FAILED_MESSAGE,
  approveAgentAuthorization,
  createAgentAuthorization,
  denyAgentAuthorization,
  logoutPlatformSession,
  type AgentAuthorizationConsent,
  type AgentAuthorizationWrite,
  type AgentScope,
  type AuthorizationRoute,
  type AgentWriteScope,
} from '@/lib/api';
import { describeScopes, scopesUpToLevel } from '@/lib/agents';
import {
  FAILURE_MESSAGES,
  checkedPersonalRedirect,
  personalClient,
  personalClientOf,
  personalTargetLabel,
  personalTargetOfConsent,
  FULL_CONTROL_SCOPE,
  NEVER_DOES,
  anyRequiresPasskey,
  formatSeconds,
  fullControlRefusalLabel,
  requiresPasskey,
  toggleWriteScope,
  checkedRedirect,
  classifyConsentFailure,
  consentClientName,
  environmentLabel,
  exclusionLabel,
  formatConsentDateTime,
  grantableLevels,
  isTerminalFailure,
  needsSignIn,
  redirectErrorMessage,
  scopeDescription,
  type ConsentClient,
  type ConsentFailure,
} from '@/lib/agent-authorization';
import { isPersonalAuthorizationQuery } from '@/lib/operator-access';
import { assignLocation } from '@/lib/browser';
import {
  MCP_AUTHORIZE_PATH,
  clearPendingAuthorizationQuery,
  readPendingAuthorization,
  savePendingAuthorizationQuery,
} from '@/lib/mcp-consent';
import { useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { StepUpDialog } from '@/components/StepUpDialog';
import { useAuthStore } from '@/stores/auth';
import { AuthCard } from './AuthCard';

/**
 * Consentimento de concessão do MCP (`/mcp/autorizar`; change platform-mcp-server, S-2.16a, D28
 * "Consentimento: página `/mcp/autorizar`").
 *
 * O "Authenticate" do perfil na página de MCP do Hermes abre esta rota com a query do
 * `/authorize`. A página guarda a query crua no `sessionStorage` (para sobreviver ao login), tira
 * a query da barra de endereço, manda a query à API (`POST /platform/agent-authorizations`) e
 * apaga a chave depois do uso. O que aparece na tela vem só do DTO validado pelo servidor: nenhum
 * parâmetro da query vai para o DOM. Aprovar ou negar devolve a `redirectUrl` montada pelo
 * servidor, e a página faz `location.assign` dela sem guardar, mostrar nem registrar (ela leva o
 * code). Nunca há redirecionamento para a `redirect_uri` da query.
 *
 * Fora do `Shell`, atrás do `McpAuthorizeRoute`, e só para Admin; o anti-frame vem do conf
 * próprio do nginx do platform-admin (S-2.18). A API revalida tudo: conta Admin no banco, sessão
 * por senha e TOTP (o pedido exige login recente), dono do pedido, nível e catálogo.
 *
 * Variante pessoal (change platform-mcp-operator-access, PR 8.3, OA-D3): quando o `client_id` da
 * query guardada é de um cliente `Operator` (`op-<tag>-<32 hex>`), a tela vira "Autorizar o seu
 * Claude Code" e fala só com a rota pessoal (`/platform/operator-authorizations`), de qualquer
 * papel, inclusive Admin. Quem não é Admin com qualquer outro `client_id` vê a página de erro de
 * sempre, sem nenhum POST. A API decide o resto: só a conta titular grava, aprova ou nega o pedido
 * (`request_not_yours` para outra conta, Admin inclusive), e com o caminho do operador desligado o
 * pedido recebe `invalid_client`. A aprovação pede o step-up (propósito
 * `operator_authorization.approve`). Desde o login nativo (OA-D12), o mesmo `client_id` serve ao
 * Claude Code e ao conector do claude.ai: a tela mostra o destino pelo `redirectTarget` do servidor
 * ("Claude Code neste computador" ou "claude.ai (conector web)") e o aviso de que só se aprova o
 * login que a própria pessoa iniciou agora, e a volta só vai à `redirectUrl` da API, conferida
 * contra a lista fechada (`checkedPersonalRedirect`): o loopback `localhost` ou `127.0.0.1` com porta
 * e `/callback`, ou `https://claude.ai/api/mcp/auth_callback`.
 *
 * Aprovar pede o código TOTP atual do Admin no `StepUpDialog` (step-up, tarefa 7.3 da change
 * platform-backoffice-hardening): o corpo (nível e `catalogVersion`) é congelado ao abrir o modal e
 * sai com o código em `X-Platform-Step-Up`. Código errado, já usado, o freio de tentativas e as
 * vagas tomadas deixam o modal aberto com a mensagem; o 423 da conta bloqueada fecha o modal e
 * mostra o bloqueio na página, sem mandar ao login (como nas outras ações com step-up). Negar não
 * pede código.
 *
 * Escrita (change platform-mcp-writes, tarefa 1.18): para o perfil que pode escrever, a API manda a
 * seção `write`. Sem a autorização preparada em `/agentes` pela mesma conta e sessão, a tela mostra
 * só a leitura e o aviso "Nenhuma escrita foi preparada para este perfil." (a API já registrou o
 * alerta). Com a preparação, só a escrita preparada aparece, já marcada e só para baixo; cada escopo
 * de risco Alto pede a sua caixa, o controle total pede o nome do perfil digitado, e o Aprovar com
 * escrita direta ou controle total aceita só passkey.
 */

export const CONSENT_WARNING =
  'Só aprove se você acabou de clicar em Authenticate na página de MCP do Hermes, neste perfil';
export const NOT_ADMIN_MESSAGE =
  'Só um Admin aprova a conexão de um agente do Hermes. Nenhum pedido foi enviado, e o pedido guardado nesta aba foi descartado.';
export const MISSING_REQUEST_MESSAGE =
  'Nenhum pedido de autorização aberto nesta aba. Comece pelo Hermes: MCP → perfil → vaptapi → Authenticate.';
/** Aviso fixo da variante pessoal (change platform-mcp-operator-access). */
export const PERSONAL_CONSENT_WARNING = 'Só aprove se você mesmo iniciou este login agora.';
export const PERSONAL_TITLE = 'Autorizar o seu Claude Code';
/** Título da variante pessoal quando a volta é o conector do claude.ai (OA-D12). */
export const PERSONAL_WEB_TITLE = 'Autorizar o seu conector do claude.ai';

/** Entrada da rota: guarda a query que chegou, tira-a da URL e decide quem pode seguir. */
export function McpAuthorizePage() {
  const { search } = useLocation();
  const isAdmin = useAuthStore((s) => s.operator?.role === 'Admin');

  if (search) return <ArrivalHandoff search={search} />;
  return <ConsentGate isAdmin={isAdmin} />;
}

/**
 * Chegada com query: guarda a query crua no `sessionStorage` e troca a URL por `/mcp/autorizar`,
 * sem query, tirando-a da barra de endereço e do histórico. Nada da query é renderizado. Quem não
 * é Admin passa pela guarda da variante pessoal, que apaga a chave se a conta não puder seguir.
 */
function ArrivalHandoff({ search }: { search: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    savePendingAuthorizationQuery(search);
    navigate(MCP_AUTHORIZE_PATH, { replace: true });
  }, [search, navigate]);
  return (
    <AuthCard title="Autorização de agente">
      <p className="muted">Carregando…</p>
    </AuthCard>
  );
}

/**
 * Escolhe a rota pelo `client_id` da query guardada, sem POST e sem ler rota de capacidade: o pedido
 * do Claude Code pessoal vai à rota pessoal, de qualquer papel; o do Hermes, só de Admin. Quem não é
 * Admin com outro `client_id` fica na página de erro, com a chave apagada.
 */
function ConsentGate({ isAdmin }: { isAdmin: boolean }) {
  const [personal] = useState(() => isPersonalAuthorizationQuery(readPendingAuthorization()?.query));
  if (personal) return <ConsentFlow route="operator" />;
  if (!isAdmin) return <NotAdminPage />;
  return <ConsentFlow route="agent" />;
}

/** Operador sem papel Admin: página de erro, nenhum POST e a chave apagada. Sem desvio para /tenants. */
function NotAdminPage() {
  useEffect(() => {
    clearPendingAuthorizationQuery();
  }, []);
  return (
    <AuthCard title="Autorização de agente">
      <div className="error-banner" role="alert">
        {NOT_ADMIN_MESSAGE}
      </div>
      <Link to="/">Voltar ao backoffice</Link>
    </AuthCard>
  );
}

/** O pedido é do cliente pessoal de um operador? Ausente (API anterior) é perfil do Hermes. */
function isPersonal(consent: Pick<AgentAuthorizationConsent, 'clientKind'>): boolean {
  return consent.clientKind === 'Operator';
}

type LoadState =
  | { kind: 'missing' }
  | { kind: 'loading' }
  | { kind: 'consent'; consent: AgentAuthorizationConsent }
  | { kind: 'leaving'; message: string }
  | { kind: 'failed'; failure: ConsentFailure };

interface Locks {
  approve: boolean;
  deny: boolean;
}

/** O que o Admin confirmou ao abrir o modal do Aprovar: o pedido mostrado, o nível e a escrita escolhidos. */
interface ApprovalPayload {
  consent: AgentAuthorizationConsent;
  scopes: AgentScope[];
  write: AgentWriteScope[];
  confirmedScopes: AgentWriteScope[];
  profileConfirmation?: string;
}

/** A escrita marcada na tela (só da preparada), as caixas de risco Alto e o nome digitado. */
interface WriteChoice {
  scopes: AgentWriteScope[];
  full: boolean;
  confirmed: AgentWriteScope[];
  profileTyped: string;
}

const EMPTY_WRITE: WriteChoice = { scopes: [], full: false, confirmed: [], profileTyped: '' };

/** A escolha inicial: toda a escrita concedível marcada (só é possível reduzir), sem as confirmações. */
function initialWrite(write: AgentAuthorizationWrite | null | undefined): WriteChoice {
  if (!write?.prepared) return EMPTY_WRITE;
  return {
    scopes: write.grantableScopes.map((s) => s.scope),
    full: write.fullControl?.offered === true,
    confirmed: [],
    profileTyped: '',
  };
}

/** Os escopos de escrita que saem no approve: o pacote sozinho, ou os marcados. */
function chosenWrite(choice: WriteChoice): AgentWriteScope[] {
  return choice.full ? [FULL_CONTROL_SCOPE] : choice.scopes;
}

/** Cada escopo de risco Alto escolhido tem a caixa marcada, e o controle total tem o nome digitado. */
function writeConfirmed(choice: WriteChoice, profile: string): boolean {
  const chosen = chosenWrite(choice);
  const highRisk = chosen.filter(requiresPasskey);
  if (!highRisk.every((s) => choice.confirmed.includes(s))) return false;
  return !choice.full || choice.profileTyped === profile;
}

function ConsentFlow({ route }: { route: AuthorizationRoute }) {
  const personalRoute = route === 'operator';
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const myName = useAuthStore((s) => s.operator?.fullName ?? null);
  // A query do pedido só em memória do componente: nunca renderizada, regravada no
  // sessionStorage se a API pedir login de novo. A regravação leva o instante da chegada do
  // pedido, e não o de agora: o prazo de 10 min não recomeça a cada recusa de sessão.
  const [pending] = useState(readPendingAuthorization);
  const [load, setLoad] = useState<LoadState>(() => (pending ? { kind: 'loading' } : { kind: 'missing' }));
  const [reloadTick, setReloadTick] = useState(0);
  const [level, setLevel] = useState<number | null>(null);
  const [writeChoice, setWriteChoice] = useState<WriteChoice>(EMPTY_WRITE);
  const [mutating, setMutating] = useState(false);
  const [locks, setLocks] = useState<Locks>({ approve: false, deny: false });
  const [actionFailure, setActionFailure] = useState<ConsentFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const busy = useRef(false);

  function keepPending() {
    if (pending) savePendingAuthorizationQuery(pending.query, pending.savedAt);
  }

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;
    createAgentAuthorization({ query: pending.query }, route)
      .then((response) => {
        if (cancelled) return;
        clearPendingAuthorizationQuery();
        if (response.kind === 'redirect') {
          // Erro depois da redirect_uri: mostra o motivo e volta ao cliente pela URL do servidor. O
          // DTO não diz o tipo do cliente: a rota pessoal só aceita o do operador, e a volta dele
          // só pode ser a lista fechada do OA-D12 (o Claude Code ou o claude.ai).
          const target = personalRoute
            ? checkedPersonalRedirect(response.redirectUrl)
            : checkedRedirect(response.redirectUrl);
          if (!target) {
            setLoad({ kind: 'failed', failure: { kind: 'unsafe_redirect', message: FAILURE_MESSAGES.unsafe_redirect } });
            return;
          }
          const client: ConsentClient = personalRoute ? personalClientOf(target) : 'hermes';
          setLoad({ kind: 'leaving', message: redirectErrorMessage(response.error, client) });
          assignLocation(target);
          return;
        }
        // A rota pessoal só devolve pedido do cliente `Operator` da própria conta (a API confere o
        // titular), e a do agente, só de perfil do Hermes. O que vier trocado não é mostrado.
        if (personalRoute !== isPersonal(response)) {
          setLoad({ kind: 'failed', failure: { kind: 'terminal', message: FAILURE_MESSAGES.personal_only } });
          return;
        }
        const levels = grantableLevels(response);
        setLevel(levels.length > 0 ? levels[levels.length - 1] : null);
        setWriteChoice(initialWrite(response.write));
        setLocks({ approve: false, deny: false });
        setLoad({ kind: 'consent', consent: response });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const classified = classifyConsentFailure(err);
        const failure: ConsentFailure =
          personalRoute && classified.kind === 'invalid_client'
            ? { kind: 'invalid_client', message: FAILURE_MESSAGES.personal_invalid_client }
            : classified;
        if (needsSignIn(failure)) savePendingAuthorizationQuery(pending.query, pending.savedAt);
        else clearPendingAuthorizationQuery();
        setLoad({ kind: 'failed', failure });
      });
    return () => {
      cancelled = true;
    };
  }, [pending, reloadTick, route, personalRoute]);

  async function signInAgain() {
    keepPending();
    setSignOutError(null);
    try {
      await logoutPlatformSession();
    } catch {
      // Com a sessão viva no cookie, ir ao login fingiria que saiu: um carregamento sem login
      // entraria de novo nela. Fica na página, com o motivo.
      setSignOutError(LOGOUT_FAILED_MESSAGE);
      return;
    }
    clearAuth();
    navigate('/login', { replace: true });
  }

  function leave(url: unknown, consent: AgentAuthorizationConsent): boolean {
    const personal = isPersonal(consent);
    const destination = personalTargetOfConsent(consent);
    const target = personal
      ? checkedPersonalRedirect(url, { host: consent.redirectHost, target: destination })
      : checkedRedirect(url, consent.redirectHost);
    if (!target) return false;
    // A URL leva o code: não vai para estado, storage nem log, só para o navegador.
    setLoad({
      kind: 'leaving',
      message: `Voltando ao ${consentClientName(personal ? personalClient(destination) : 'hermes')}…`,
    });
    assignLocation(target);
    return true;
  }

  function handleDecisionFailure(err: unknown, decision: 'approve' | 'deny') {
    const failure = classifyConsentFailure(err, 'decision');
    if (failure.kind === 'catalog_changed') {
      // Recarrega o pedido com a mesma query, que segue em memória; o Admin confere e aprova de novo.
      setNotice(failure.message);
      setActionFailure(null);
      setLoad({ kind: 'loading' });
      setReloadTick((t) => t + 1);
      return;
    }
    if (needsSignIn(failure)) keepPending();
    setActionFailure(failure);
    setLocks((current) =>
      isTerminalFailure(failure)
        ? { approve: true, deny: true }
        : failure.kind === 'mcp_disabled' && decision === 'approve'
          ? { ...current, approve: true }
          : current,
    );
  }

  /** Sai para o Hermes pela `redirectUrl` da API, ou trava a página se ela não for a esperada. */
  function leaveOrLock(url: unknown, consent: AgentAuthorizationConsent): boolean {
    const left = leave(url, consent);
    if (!left) {
      setActionFailure({ kind: 'unsafe_redirect', message: FAILURE_MESSAGES.unsafe_redirect });
      setLocks({ approve: true, deny: true });
    }
    return left;
  }

  /**
   * O approve, chamado pelo modal com o código TOTP e o corpo congelado ao abrir o modal. A recusa
   * sobe para o `useStepUpDialog`: o step-up recusado (código errado ou já usado, freio de
   * tentativas, vagas tomadas) mantém o modal aberto; o resto fecha o modal e vai para
   * `handleDecisionFailure`.
   */
  async function approveWithCode(proof: StepUpProof, payload: ApprovalPayload) {
    if (busy.current) return;
    busy.current = true;
    setMutating(true);
    let left = false;
    try {
      const response = await approveAgentAuthorization(
        payload.consent.requestId,
        {
          scopes: [...payload.scopes, ...payload.write],
          catalogVersion: payload.consent.catalogVersion,
          ...(payload.write.length > 0 ? { confirmedScopes: payload.confirmedScopes } : {}),
          ...(payload.profileConfirmation !== undefined ? { profileConfirmation: payload.profileConfirmation } : {}),
        },
        proof,
        route,
      );
      left = leaveOrLock(response?.redirectUrl, payload.consent);
    } finally {
      // Saindo para o Hermes, os botões ficam travados até a página trocar.
      if (!left) {
        busy.current = false;
        setMutating(false);
      }
    }
  }

  const stepUp = useStepUpDialog<ApprovalPayload>(approveWithCode, (err) => handleDecisionFailure(err, 'approve'), {
    // Passkey (C14): o desafio preso a este pedido de autorização.
    passkeyTarget: (payload) =>
      stepUpTarget(personalRoute ? 'operatorAuthorizationApprove' : 'agentAuthorizationApprove', payload.consent.requestId),
  });

  /** Aprovar abre o modal do código; nada sai antes do código. */
  function startApproval(consent: AgentAuthorizationConsent) {
    if (busy.current || stepUp.open || level === null) return;
    setActionFailure(null);
    setNotice(null);
    const write = chosenWrite(writeChoice);
    if (write.length > 0 && !writeConfirmed(writeChoice, consent.profile)) return;
    stepUp.start({
      consent,
      scopes: scopesUpToLevel(level),
      write,
      confirmedScopes: writeChoice.confirmed.filter((s) => write.includes(s)),
      ...(writeChoice.full ? { profileConfirmation: writeChoice.profileTyped } : {}),
    });
  }

  async function deny(consent: AgentAuthorizationConsent) {
    if (busy.current || stepUp.open) return;
    busy.current = true;
    setMutating(true);
    setActionFailure(null);
    setNotice(null);
    let left = false;
    try {
      const response = await denyAgentAuthorization(consent.requestId, route);
      left = leaveOrLock(response?.redirectUrl, consent);
    } catch (err) {
      handleDecisionFailure(err, 'deny');
    } finally {
      if (!left) {
        busy.current = false;
        setMutating(false);
      }
    }
  }

  if (load.kind === 'missing') {
    return (
      <AuthCard title="Autorização de agente">
        <div className="info-banner" role="status">
          {MISSING_REQUEST_MESSAGE}
        </div>
        <Link to="/">Voltar ao backoffice</Link>
      </AuthCard>
    );
  }

  if (load.kind === 'loading') {
    return (
      <AuthCard title="Autorização de agente">
        {notice && (
          <div className="info-banner" role="status">
            {notice}
          </div>
        )}
        <p className="muted">Carregando o pedido…</p>
      </AuthCard>
    );
  }

  if (load.kind === 'leaving') {
    return (
      <AuthCard title="Autorização de agente">
        <div className="info-banner" role="status">
          {load.message}
        </div>
      </AuthCard>
    );
  }

  if (load.kind === 'failed') {
    return (
      <AuthCard title="Autorização de agente">
        <FailureBanner failure={load.failure} onSignInAgain={() => void signInAgain()} />
        {signOutError && (
          <div className="error-banner" role="alert">
            {signOutError}
          </div>
        )}
        <Link to="/">Voltar ao backoffice</Link>
      </AuthCard>
    );
  }

  const consent = load.consent;
  const personal = isPersonal(consent);
  const destination = personalTargetOfConsent(consent);
  const clientName = consentClientName(personal ? personalClient(destination) : 'hermes');
  const personalTitle = destination === 'claude_web' ? PERSONAL_WEB_TITLE : PERSONAL_TITLE;
  const levels = grantableLevels(consent);
  // Com o modal do código aberto, nível e botões ficam travados: o que sai é o que o Admin
  // confirmou ao abrir o modal.
  const chosen = chosenWrite(writeChoice);
  const writeReady = chosen.length === 0 || writeConfirmed(writeChoice, consent.profile);
  const passkeyOnly = anyRequiresPasskey(chosen);
  const approveDisabled =
    mutating || stepUp.open || locks.approve || level === null || !levels.includes(level) || !writeReady;
  const denyDisabled = mutating || stepUp.open || locks.deny;

  return (
    <AuthCard
      title={
        personal
          ? personalTitle
          : `Autorizar o perfil ${consent.profile} do Hermes de ${environmentLabel(consent.environmentTag)}`
      }
      maxWidth={620}
    >
      <div className="warning-banner" role="note" style={{ marginBottom: 16 }}>
        {personal ? PERSONAL_CONSENT_WARNING : CONSENT_WARNING}
      </div>

      {notice && (
        <div className="info-banner" role="status">
          {notice}
        </div>
      )}
      {actionFailure && <FailureBanner failure={actionFailure} onSignInAgain={() => void signInAgain()} />}
      {signOutError && (
        <div className="error-banner" role="alert">
          {signOutError}
        </div>
      )}

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', margin: '0 0 16px' }}>
        {personal ? (
          // Sem escolha de conta: a concessão é sempre da conta logada, que o servidor conferiu.
          <Fact label="Sua conta">{myName ?? '—'}</Fact>
        ) : (
          <Fact label="Perfil">{consent.profile}</Fact>
        )}
        <Fact label="Ambiente">
          {environmentLabel(consent.environmentTag)} <span className="mono">({consent.environmentTag})</span>
        </Fact>
        <Fact label="Cliente OAuth">
          <span className="mono">{consent.oauthClientId}</span>
        </Fact>
        {personal && (
          // O destino decidido pelo servidor a partir da redirect_uri validada (OA-D12).
          <Fact label="Destino">
            <strong data-testid="personal-destination">{personalTargetLabel(destination)}</strong>
          </Fact>
        )}
        <Fact label="Volta para">
          <span className="mono">{consent.redirectHost}</span>
        </Fact>
        <Fact label="Pedido em">{formatConsentDateTime(consent.requestedAt)}</Fact>
        <Fact label="Pedido vence em">{formatConsentDateTime(consent.expiresAt)}</Fact>
        <Fact label="Concessão vale até">
          {formatConsentDateTime(consent.grantExpiresAt)} ({consent.grantMaxDays} dias)
        </Fact>
        {personal && consent.presenceHours ? (
          <Fact label="Presença">
            a renovação só vale nas {consent.presenceHours} horas seguintes a esta aprovação; depois, o navegador
            abre de novo
          </Fact>
        ) : null}
      </dl>

      <ScopeSection title={`Pedido pelo ${clientName}`}>
        {consent.requestedScopes.length === 0 ? (
          <p className="muted">Nenhum escopo.</p>
        ) : (
          <ul>
            {consent.requestedScopes.map((scope) => (
              <li key={scope}>
                <span className="mono">{scope}</span>
                {scopeDescription(scope) && <span className="muted"> — {scopeDescription(scope)}</span>}
              </li>
            ))}
          </ul>
        )}
      </ScopeSection>

      <ScopeSection title="Concedível">
        {consent.grantableScopes.length === 0 ? (
          <p className="muted">Nada do pedido é concedível; só é possível negar.</p>
        ) : (
          <ul>
            {consent.grantableScopes.map((scope) => (
              <li key={scope.scope}>
                <span className="mono">{scope.scope}</span> — {scope.label}
              </li>
            ))}
          </ul>
        )}
      </ScopeSection>

      <ScopeSection title="Fora da concessão">
        {consent.excludedScopes.length === 0 ? (
          <p className="muted">Nenhum escopo pedido ficou de fora.</p>
        ) : (
          <ul>
            {consent.excludedScopes.map((excluded) => (
              <li key={excluded.scope}>
                <span className="mono">{excluded.scope}</span> — {exclusionLabel(excluded.reason)}
              </li>
            ))}
          </ul>
        )}
      </ScopeSection>

      <ScopeSection title="Concessão vigente">
        {consent.currentGrant ? (
          <p data-testid="current-grant">
            Será substituída: {describeScopes(consent.currentGrant.scopes)}, até{' '}
            {formatConsentDateTime(consent.currentGrant.expiresAt)}, aprovada por {consent.currentGrant.approvedByName}.
          </p>
        ) : (
          <p className="muted">
            {personal ? 'Você não tem concessão vigente neste ambiente.' : 'Este perfil não tem concessão vigente.'}
          </p>
        )}
      </ScopeSection>

      {levels.length > 0 && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={{ fontWeight: 600, marginBottom: 6 }}>Nível da concessão (só é possível reduzir)</legend>
          {levels.map((option) => (
            <label key={option} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <input
                type="radio"
                name="consent-level"
                value={option}
                checked={level === option}
                disabled={mutating || stepUp.open || locks.approve}
                onChange={() => setLevel(option)}
              />
              {describeScopes(scopesUpToLevel(option))}
            </label>
          ))}
        </fieldset>
      )}

      {consent.write && (
        <WriteSection
          write={consent.write}
          profile={consent.profile}
          choice={writeChoice}
          disabled={mutating || stepUp.open || locks.approve}
          onChange={setWriteChoice}
        />
      )}

      <div className="btn-row" style={{ display: 'flex', gap: 8 }}>
        {/* Aprovar abre o modal do código do autenticador (step-up); o approve só sai de lá. */}
        <button type="button" className="btn" disabled={approveDisabled} onClick={() => startApproval(consent)}>
          {mutating ? 'Enviando…' : 'Aprovar'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={denyDisabled}
          onClick={() => void deny(consent)}
        >
          Negar
        </button>
      </div>

      {stepUp.open && level !== null && (
        <StepUpDialog
          title={personal ? personalTitle : `Aprovar o perfil ${consent.profile}`}
          description={
            <>
              <p style={{ margin: '0 0 8px' }}>
                {personal ? (
                  <>
                    {destination === 'claude_web' ? 'O seu conector do claude.ai' : 'O seu Claude Code'} recebe uma
                    concessão de <strong>{describeScopes(scopesUpToLevel(level))}</strong>,
                    só de leitura, válida por {consent.grantMaxDays} dias
                    {consent.currentGrant ? ', e a sua concessão vigente é substituída' : ''}.
                  </>
                ) : (
                  <>
                    O Hermes de {environmentLabel(consent.environmentTag)} recebe uma concessão de{' '}
                    <strong>{describeScopes(scopesUpToLevel(level))}</strong>, válida por {consent.grantMaxDays} dias
                    {consent.currentGrant ? ', e a concessão vigente deste perfil é substituída' : ''}.
                  </>
                )}
              </p>
              {chosen.length > 0 && (
                <p style={{ margin: '0 0 8px' }}>
                  Com escrita:{' '}
                  <strong>{chosen.includes(FULL_CONTROL_SCOPE) ? 'controle total' : chosen.join(', ')}</strong>.
                </p>
              )}
              <p style={{ margin: 0 }}>
                {passkeyOnly
                  ? 'Escrita direta e controle total pedem a confirmação por passkey; o código do autenticador não vale aqui.'
                  : 'Confirme com o código atual do autenticador. Recovery code não vale aqui.'}
              </p>
            </>
          }
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
          passkeyOnly={passkeyOnly}
        />
      )}
    </AuthCard>
  );
}

/**
 * A seção de escrita do consentimento. Sem preparação, só o aviso. Com preparação, os escopos
 * preparados, os que ficaram de fora com o motivo, as caixas da escrita concedível (só para baixo), a
 * confirmação de cada escopo de risco Alto e o controle total com o nome do perfil digitado.
 */
function WriteSection({
  write,
  profile,
  choice,
  disabled,
  onChange,
}: {
  write: AgentAuthorizationWrite;
  profile: string;
  choice: WriteChoice;
  disabled: boolean;
  onChange: (choice: WriteChoice) => void;
}) {
  if (!write.prepared) {
    return (
      <section aria-label="Escrita" style={{ marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Escrita</h3>
        <div className="warning-banner" role="alert" data-testid="write-unprepared">
          {write.notice ?? 'Nenhuma escrita foi preparada para este perfil.'} Só a leitura pode ser aprovada. Se você
          não clicou em Authenticate neste perfil agora, negue. Para escrever, prepare a autorização em Agentes de IA e
          recomece pelo Hermes.
        </div>
      </section>
    );
  }

  const allowed = write.grantableScopes.map((s) => s.scope);
  const highRisk = chosenWrite(choice).filter(requiresPasskey);
  const full = write.fullControl;

  function toggle(scope: AgentWriteScope, checked: boolean) {
    const scopes = toggleWriteScope(choice.scopes, scope, checked, allowed);
    onChange({ ...choice, scopes, confirmed: choice.confirmed.filter((s) => scopes.includes(s) || s === FULL_CONTROL_SCOPE) });
  }

  function confirm(scope: AgentWriteScope, checked: boolean) {
    const confirmed = checked ? [...choice.confirmed, scope] : choice.confirmed.filter((s) => s !== scope);
    onChange({ ...choice, confirmed });
  }

  return (
    <section aria-label="Escrita" style={{ marginBottom: 12 }} data-testid="write-section">
      <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Escrita preparada em Agentes de IA</h3>
      <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
        Preparada em {write.intentCreatedAt ? formatConsentDateTime(write.intentCreatedAt) : '—'}; o pedido chegou{' '}
        {formatSeconds(write.secondsSinceIntent)} depois. Só a escrita preparada aparece, e só é possível reduzir.
      </p>

      {allowed.length === 0 && !full?.offered ? (
        <p className="muted">Nada da escrita preparada é concedível neste ambiente; aprove só a leitura ou negue.</p>
      ) : (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 8px' }} disabled={disabled || choice.full}>
          <legend style={{ fontWeight: 600, marginBottom: 6 }}>Escopos de escrita</legend>
          {write.grantableScopes.map((item) => (
            <label key={item.scope} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={choice.scopes.includes(item.scope)}
                onChange={(e) => toggle(item.scope, e.target.checked)}
              />
              <span>
                <span className="mono">{item.scope}</span> — {item.label}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {write.excludedScopes.length > 0 && (
        <ul data-testid="write-excluded">
          {write.excludedScopes.map((excluded) => (
            <li key={excluded.scope}>
              <span className="mono">{excluded.scope}</span> — {exclusionLabel(excluded.reason)}
            </li>
          ))}
        </ul>
      )}

      {full && (
        <div style={{ margin: '8px 0' }} data-testid="full-control">
          {full.offered ? (
            <>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={choice.full}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...choice, full: e.target.checked, profileTyped: '' })}
                />
                Controle total por 8 h (até 22:00), congelado nesta lista
              </label>
              <ul>
                {full.scopes.map((item) => (
                  <li key={item.scope}>
                    <span className="mono">{item.scope}</span> — {item.label}
                  </li>
                ))}
              </ul>
              <p className="muted" style={{ margin: '0 0 4px', fontSize: 13 }}>
                Nem com controle total o agente pode: {NEVER_DOES.join('; ')}.
              </p>
              {choice.full && (
                <div className="field">
                  <label htmlFor="full-control-profile">Digite o nome do perfil ({profile}) para confirmar</label>
                  <input
                    id="full-control-profile"
                    type="text"
                    autoComplete="off"
                    value={choice.profileTyped}
                    disabled={disabled}
                    onChange={(e) => onChange({ ...choice, profileTyped: e.target.value })}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="info-banner" role="status">
              {fullControlRefusalLabel(full.refusal)}
            </div>
          )}
        </div>
      )}

      {highRisk.length > 0 && (
        <fieldset style={{ border: 0, padding: 0, margin: '8px 0' }} disabled={disabled}>
          <legend style={{ fontWeight: 600, marginBottom: 6 }}>Confirmações de risco alto</legend>
          {highRisk.map((scope) => (
            <label key={scope} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={choice.confirmed.includes(scope)}
                onChange={(e) => confirm(scope, e.target.checked)}
              />
              <span>
                Entendo que, com <span className="mono">{scope}</span>, o agente age sozinho, dentro da ordem de
                serviço e dos limites.
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="muted">{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  );
}

function ScopeSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} style={{ marginBottom: 12 }}>
      <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>{title}</h3>
      {children}
    </section>
  );
}

function FailureBanner({ failure, onSignInAgain }: { failure: ConsentFailure; onSignInAgain: () => void }) {
  if (needsSignIn(failure)) {
    return (
      <div className="warning-banner" role="alert">
        {failure.message}{' '}
        <button type="button" className="btn btn-secondary btn-sm" onClick={onSignInAgain}>
          Entrar de novo
        </button>
        {failure.kind === 'reauth' && (
          <p style={{ margin: '8px 0 0', fontSize: 13 }}>
            Entrou por recovery code? <Link to="/profile?regenerate=1">Gere códigos novos no perfil</Link> e entre
            de novo com o código do autenticador.
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={failure.kind === 'unavailable' ? 'info-banner' : 'error-banner'} role="alert">
      {failure.message}
    </div>
  );
}
